import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { css } from "styled-components/macro";
import { useRef } from "react";

import Slider from "react-slick"; // ✅ import slider
import "slick-carousel/slick/slick-theme.css";
import VectorDesign from "images/Vector1.svg";
import GrayDesign from "images/blueDots.svg";
import LineDesign from "images/greenLines.png";

import Header from "../headers/light.js";
import AnimatedText from "./AnimatedText.js";
import OurClients from "../clients/Clients.js";
import { LinkButton as PrimaryButtonBase } from "components/misc/Buttons.js";

const IconImage = styled.img`
  ${tw`w-30 sm:w-32 mt-3 mb-0`}
`;

const Container = tw.div`relative pt-0 mt-12 lg:px-0 xs:px-6`;
const TwoColumn = tw.div`flex flex-col lg:flex-row lg:items-center max-w-screen-xl mx-auto xs:pt-5 md:pt-5`;
const LeftColumn = tw.div`relative lg:w-6/12 text-left max-w-lg mx-auto lg:max-w-none lg:text-left`;
const RightColumn = tw.div`relative mt-12 lg:mt-0 flex-1 flex flex-col justify-center lg:self-end`;

// Updated Heading with ::before and ::after support for images
// Usage: Apply additional styles via css prop with Tailwind's arbitrary value syntax for bg-[url(...)] on before/after
// Example: <Heading css={css`before:content-[''] before:absolute before:left-[-30px] ...`}>Your Title</Heading>
const Heading = tw.h1`font-bold text-3xl md:text-3xl lg:text-3xl xl:text-3xl text-black leading-tight relative inline-block`;

const Paragraph = tw.p`pr-0 my-5 lg:my-0 text-[1.4rem] leading-[2.0rem]`;
const ParagraphNew = tw.p`my-0 lg:my-0 text-base xl:text-lg`;
const seminarText = tw.p`mt-2 lg:mt-2 text-base xl:text-lg`;
const UpcomingBlock = tw.div`flex items-center py-4 space-x-6`;

const LinkButton = styled(PrimaryButtonBase)([
  tw`text-blue-700 font-bold !text-lg inline-block mx-auto md:mx-0 rounded-full`,
]);
const Content = tw.p`text-blue-700 font-bold !text-lg inline-block mx-auto md:mx-0 rounded-full`;
const Highlight = tw.span`text-left text-base xl:text-lg text-[#dc3188] font-bold pl-0 pr-2`;

const Actions = styled.div`
  ${tw`relative max-w-md mx-auto lg:mx-0`}
  button {
    ${tw`w-8/12 bg-[#01325b] text-gray-100 lg:ml-0 lg:mr-2 my-4 sm:my-2 py-4 
        flex items-center justify-center sm:w-40 sm:leading-none focus:outline-none hover:bg-secondary-900 
        transition duration-300`}
  }
`;

const IllustrationContainer = tw.div`
    relative flex justify-center lg:justify-end items-center
  `;

const IconImage1 = tw.img`
    absolute w-auto h-auto md:w-auto md:h-auto
  `;

const ClientSection = tw.section`text-center mt-5 mb-10`;

// ✅ Full Width Slider Container
const FullWidthSlider = styled.div`
  width: 100%;
  overflow: hidden;

  .slide-wrap {
    width: 100%;
    height: 320px;
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

  .slide-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Tablet */
  @media (min-width: 768px) {
    .slide-wrap {
      height: 420px;
    }
  }

  /* Desktop */
  @media (min-width: 1024px) {
    .slide-wrap {
      height: 520px;
    }
  }

  /* Slider dots */
  .slick-dots {
    bottom: -30px;
  }

  .slick-dots li {
    margin: 0 6px;
  }

  .slick-dots li button:before {
    font-size: 10px;
    color: #ccc;
    opacity: 1;
    transition: all 0.3s ease;
  }

  .slick-dots li.slick-active button:before {
    font-size: 16px;
    color: #0f4640;
    opacity: 1;
  }
`;

export default ({
  roundedHeaderButton,
  mainTitle = "",
  subTitle = "",
  paragraph = "",
  imageSrc = "",
  buttonText = "",
  buttonUrl = "https://wa.me/918826622806/?text=Hi, I want to know more about German Courses & Its Benefits..",
  showClient = true,
  knowmoreUrl,
  otherText,
  mainColorText,
  imageSrc1 = VectorDesign,
  imageSrc2 = GrayDesign,
  imageSrc3 = LineDesign,
}) => {
  // ✅ slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplaySpeed: 2500,
  };

  // ✅ slider images - replace with your banners
  const sliderImages = [
    "https://i0.wp.com/cavijenderaggarwal.com/wp-content/uploads/2025/10/top-rankers-1-scaled.jpg?fit=1536%2C391&ssl=1",
    "https://i0.wp.com/cavijenderaggarwal.com/wp-content/uploads/2025/10/Website-1500-x-382-px-4-scaled.jpg?fit=1536%2C391&ssl=1",
    "https://i0.wp.com/cavijenderaggarwal.com/wp-content/uploads/2025/10/3-New-Batch-scaled.jpg?fit=1536%2C391&ssl=1",
    "https://i0.wp.com/cavijenderaggarwal.com/wp-content/uploads/2025/10/Fast-Track-Flyer-1-scaled.jpg?fit=1536%2C391&ssl=1",
    "https://i0.wp.com/cavijenderaggarwal.com/wp-content/uploads/2025/10/4-Recorded-Classes-scaled.jpg?fit=1536%2C391&ssl=1",
  ];

  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;

    // Set motion values for parallax
    containerRef.current.style.setProperty("--x", `${x / 20}px`);
    containerRef.current.style.setProperty("--y", `${y / 20}px`);
  };

  const handleMouseLeave = () => {
    containerRef.current.style.setProperty("--x", `0px`);
    containerRef.current.style.setProperty("--y", `0px`);
  };

  return (
    <>
      <Header roundedHeaderButton={roundedHeaderButton} />

      {/* ✅ Full Width Slider under header */}
      <FullWidthSlider>
        <Slider {...sliderSettings}>
          {sliderImages.map((img, idx) => (
            <div key={idx} className="slide-wrap">
              <img src={img} alt={`slide-${idx}`} />
            </div>
          ))}
        </Slider>
      </FullWidthSlider>

      <Container>
        <TwoColumn>
          <LeftColumn>
            {mainTitle && (
              <Heading>
                {mainTitle}
                <IconImage
                  style={{ color: "#fff" }}
                  src={imageSrc1}
                  alt="vector icon"
                />
              </Heading>
            )}
            {subTitle && (
              <Paragraph
                dangerouslySetInnerHTML={{ __html: subTitle }}
              ></Paragraph>
            )}

            <UpcomingBlock>
              {knowmoreUrl && (
                <ParagraphNew>
                  <LinkButton className="exploreBtn" as="a" href={knowmoreUrl}>
                    <Content>
                      <Highlight>{mainColorText}</Highlight>
                      {otherText}
                    </Content>
                  </LinkButton>
                  <seminarText>
                    <br />
                    Get Detailed Info from Experts on NEP 2020, Prospects of
                    Foreign Language in Global Careers.
                  </seminarText>
                </ParagraphNew>
              )}
            </UpcomingBlock>

            <Actions>
              <a href={buttonUrl} target="_blank">
                <button className="bt-round">{buttonText}</button>
              </a>
            </Actions>
          </LeftColumn>

          <RightColumn>
            <IllustrationContainer
              ref={containerRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              style={{
                perspective: "1000px",
                transformStyle: "preserve-3d",
              }}
            >
              {/* Top-left icon */}
              <IconImage1
                src={imageSrc2}
                alt="Top Left Icon"
                style={{
                  top: "0",
                  left: "20%",
                  transform:
                    "translate(calc(-50% + var(--x)), calc(50% + var(--y)))",
                  transition: "transform 0.3s ease-out",
                }}
              />

              {/* Top-right icon */}
              <IconImage1
                src={imageSrc3}
                alt="Top Right Icon"
                style={{
                  top: "10%",
                  right: "10%",
                  transform:
                    "translate(calc(50% + var(--x)), calc(-50% + var(--y)))",
                  transition: "transform 0.3s ease-out",
                }}
              />

              {/* Main centered image */}
              <img
                tw="min-w-0 w-full max-w-lg xl:max-w-3xl z-10"
                src="https://qsfiles.b-cdn.net/vac-s2.png"
                alt="Design Illustration"
              />

              {/* Bottom-right icon */}
              <IconImage1
                src={imageSrc2}
                alt="Bottom Right Icon"
                style={{
                  bottom: "0",
                  right: "0",
                  transform:
                    "translate(calc(30% + var(--x)), calc(-30% + var(--y)))",
                  transition: "transform 0.3s ease-out",
                }}
              />
            </IllustrationContainer>
          </RightColumn>
        </TwoColumn>

        {showClient && (
          <>
            <AnimatedText />
            <ClientSection>
              <OurClients />
            </ClientSection>
          </>
        )}
      </Container>
    </>
  );
};
